import { createElement } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { type RouteObject } from 'react-router-dom';
import {
  PermGuard,
  ProtectedRoute,
  RootRedirect,
} from '@/app/router/ProtectedRoute';
import { AdminShellWrapper } from '@/app/AdminShellWrapper';
import DashboardPage from '@/pages/dashboard';
import ParcelsPage from '@/pages/parcels';
import ParcelDetailPage from '@/pages/parcel-detail';
import ParcelCreatePage from '@/pages/parcel-create';
import ParcelEditPage from '@/pages/parcel-edit';
import PartiesPage from '@/pages/parties';
import PartyDetailPage from '@/pages/party-detail';
import PackagesPage from '@/pages/packages';
import PackageCreatePage from '@/pages/package-create';
import PackageEditPage from '@/pages/package-edit';
import SplitsPage from '@/pages/splits';
import SplitDetailPage from '@/pages/split-detail';
import CargoIdsPage from '@/pages/cargo-ids';
import CargoIdDetailPage from '@/pages/cargo-id-detail';
import WarehousePage from '@/pages/warehouse';
import WarehouseCreatePage from '@/pages/warehouse-create';
import WarehouseEditPage from '@/pages/warehouse-edit';
import WarehouseArrivedPackagePage from '@/pages/warehouse-arrived-package';
import WarehouseArrivedParcelPage from '@/pages/warehouse-arrived-parcel';
import { WidgetsWarehouseRedirect as WarehouseRedirectPage } from '@/widgets/warehouse/modules/ui/WarehouseRedirect';
import CategoriesPage from '@/pages/categories';
import StaffPage from '@/pages/staff';
import StaffNewPage from '@/pages/staff-new';
import StaffEditPage from '@/pages/staff-edit';
import SupportStaffPage from '@/pages/support-staff';
import SupportStaffCreatePage from '@/pages/support-staff-create';
import SupportStaffEditPage from '@/pages/support-staff-edit';
import RolesPage from '@/pages/roles';
import RoleCreatePage from '@/pages/role-create';
import RoleEditPage from '@/pages/role-edit';
import PricingPage from '@/pages/pricing';
import LimitConfigPage from '@/pages/limit-config';
import ReportsPhotoPage from '@/pages/reports-photo';
import ReportsVideoPage from '@/pages/reports-video';
import ReportsProverkaPage from '@/pages/reports-proverka';
import ReturnsPage from '@/pages/returns';
import FaqsPage from '@/pages/faqs';
import SupportPage from '@/pages/support';
import SupportDetailPage from '@/pages/support/detail';
import ClientsPage from '@/pages/clients';
import ClientDetailPage from '@/pages/client-detail';
import LegacyClientsPage from '@/pages/legacy-clients';
import BroadcastPage from '@/pages/broadcast';
import NoAccessPage from '@/pages/no-access';

/**
 * Permission-gated route: requires the current staff to have `perm` in their
 * nested permissions tree (super users bypass). Combined with `wrapRole` for
 * routes that also need to keep legacy role gating (e.g. create/edit pages
 * that hardcode JAPAN_WORKER access today). For pure permission gating use
 * `wrapPerm` alone.
 */
const wrapPerm = (perm: string, node: ReactNode): ReactElement =>
  createElement(
    ProtectedRoute,
    null,
    createElement(PermGuard, {
      perm,
      children: createElement(AdminShellWrapper, null, node),
    }),
  );

/**
 * Auth-only shell wrapper (no permission/role gate). Used for `/no-access`,
 * which must always render inside the AdminShell so the user keeps access to
 * the topbar (logout) and the permission-filtered sidebar even when none of
 * their default landings are reachable.
 */
const wrapShell = (node: ReactNode): ReactElement =>
  createElement(
    ProtectedRoute,
    null,
    createElement(AdminShellWrapper, null, node),
  );

export const protectedRoutes: RouteObject[] = [
  { path: '/', element: createElement(RootRedirect) },
  { path: '/no-access', element: wrapShell(NoAccessPage) },
  { path: '/dashboard', element: wrapPerm('dashboard.view', DashboardPage) },
  { path: '/parcels', element: wrapPerm('parcels.view', ParcelsPage) },
  { path: '/parcels/create', element: wrapPerm('parcels.create', ParcelCreatePage) },
  { path: '/parcels/:id/edit', element: wrapPerm('parcels.edit', ParcelEditPage) },
  { path: '/parcels/:id', element: wrapPerm('parcels.detail', ParcelDetailPage) },
  { path: '/parties', element: wrapPerm('parties.view', PartiesPage) },
  { path: '/parties/:id', element: wrapPerm('parties.view', PartyDetailPage) },
  { path: '/packages', element: wrapPerm('packages.view', PackagesPage) },
  { path: '/packages/new', element: wrapPerm('packages.create', PackageCreatePage) },
  { path: '/packages/:id/edit', element: wrapPerm('packages.edit', PackageEditPage) },
  { path: '/splits', element: wrapPerm('splits.view', SplitsPage) },
  { path: '/splits/:id', element: wrapPerm('splits.view', SplitDetailPage) },
  { path: '/cargo-ids', element: wrapPerm('cargo_ids.view', CargoIdsPage) },
  { path: '/cargo-ids/:id', element: wrapPerm('cargo_ids.view', CargoIdDetailPage) },
  // /warehouse landing redirects to the first tab the user can see — orphan
  // posilkalar first, owned posilkalar as fallback. Both sub-paths use the
  // same shell widget which reads its active tab from the pathname.
  { path: '/warehouse', element: createElement(WarehouseRedirectPage) },
  { path: '/warehouse/orphan', element: wrapPerm('warehouse.view', WarehousePage) },
  { path: '/warehouse/owned', element: wrapPerm('parcels.list', WarehousePage) },
  { path: '/warehouse/new', element: wrapPerm('warehouse.create', WarehouseCreatePage) },
  { path: '/warehouse/:id/edit', element: wrapPerm('warehouse.edit', WarehouseEditPage) },
  { path: '/warehouse/arrived-packages', element: wrapPerm('warehouse.arrived_packages.view', WarehousePage) },
  { path: '/warehouse/arrived-packages/:id', element: wrapPerm('warehouse.arrived_packages.view', WarehouseArrivedPackagePage) },
  { path: '/warehouse/arrived-parcels', element: wrapPerm('warehouse.arrived_parcels.view', WarehousePage) },
  { path: '/warehouse/arrived-parcels/:id', element: wrapPerm('warehouse.arrived_parcels.view', WarehouseArrivedParcelPage) },
  { path: '/categories', element: wrapPerm('categories.view', CategoriesPage) },
  { path: '/staff', element: wrapPerm('staff.view', StaffPage) },
  { path: '/staff/new', element: wrapPerm('staff.create', StaffNewPage) },
  { path: '/staff/:id/edit', element: wrapPerm('staff.edit', StaffEditPage) },
  { path: '/support-staff', element: wrapPerm('support_staff.view', SupportStaffPage) },
  { path: '/support-staff/create', element: wrapPerm('support_staff.create', SupportStaffCreatePage) },
  { path: '/support-staff/:id/edit', element: wrapPerm('support_staff.edit', SupportStaffEditPage) },
  { path: '/roles', element: wrapPerm('roles.view', RolesPage) },
  { path: '/roles/new', element: wrapPerm('roles.create', RoleCreatePage) },
  { path: '/roles/:id/edit', element: wrapPerm('roles.edit', RoleEditPage) },
  { path: '/pricing', element: wrapPerm('pricing.view', PricingPage) },
  // Backend `/pinfl/limit-config` gates GET on `pricing.view` (PUT on
  // `pricing.edit`), so mirror the read perm here instead of leaving it
  // auth-only. The Save button is separately gated on `pricing.edit`.
  { path: '/limit-config', element: wrapPerm('pricing.view', LimitConfigPage) },
  { path: '/reports/photo', element: wrapPerm('reports.view', ReportsPhotoPage) },
  { path: '/reports/video', element: wrapPerm('reports.view', ReportsVideoPage) },
  { path: '/reports/proverka', element: wrapPerm('reports.view', ReportsProverkaPage) },
  { path: '/returns', element: wrapPerm('returns.view', ReturnsPage) },
  { path: '/faqs', element: wrapPerm('faqs.view', FaqsPage) },
  { path: '/support', element: wrapPerm('support.view', SupportPage) },
  { path: '/support/:userId', element: wrapPerm('support.view', SupportDetailPage) },
  { path: '/clients', element: wrapPerm('clients.view', ClientsPage) },
  { path: '/clients/:id', element: wrapPerm('clients.view', ClientDetailPage) },
  { path: '/legacy-clients', element: wrapPerm('legacy_clients.view', LegacyClientsPage) },
  { path: '/broadcast', element: wrapPerm('broadcast.view', BroadcastPage) },
];
